/**
 * Client-side fallback for recommendation blurbs (mirrors backend logic).
 */
import { formatFitScoreBadge, hasMatchOrFitScorePrefix } from "./matchScoreUtils";

function formatPriceDelta(currentPrice, altPrice) {
  const diff = (Number(altPrice) || 0) - (Number(currentPrice) || 0);
  if (Math.abs(diff) < 1) return "Same price as your current pick";
  const amount = `$${Math.abs(diff).toLocaleString()}`;
  return diff < 0 ? `${amount} less than your current pick` : `${amount} more`;
}

function buildBlurbForProduct(product, hint = {}, context = {}) {
  const { contextProduct, surface, useCase } = context;

  if (hint.reason) return String(hint.reason).slice(0, 140);
  if (hint.comparisonLabel) return String(hint.comparisonLabel).slice(0, 140);

  const matchScore = hint.matchScore ?? product?.matchScore ?? product?.displayScore;
  if (matchScore != null && Number.isFinite(Number(matchScore))) {
    const useCaseBit = useCase ? ` for ${String(useCase).toLowerCase()}` : "";
    return `${formatFitScoreBadge(matchScore)}${useCaseBit}`;
  }

  if (Array.isArray(hint.highlights) && hint.highlights.length > 0) {
    return hint.highlights[0].slice(0, 140);
  }

  if (surface === "alternatives" && contextProduct) {
    const priceLine = formatPriceDelta(contextProduct.price, product?.price);
    if (priceLine) return priceLine;
  }

  if (surface === "for_you") {
    return "Picked based on your preferences and browsing";
  }

  return "Similar option worth comparing";
}

export function buildRecommendationBlurbFallback({
  products = [],
  hintsById = {},
  contextProduct = null,
  surface = "for_you",
  searchParams = null,
}) {
  const blurbs = {};
  const useCase = searchParams?.useCase || searchParams?.primaryUseCase || null;

  products.forEach((product) => {
    const id = String(product?._id || product?.id || "");
    if (!id) return;
    const hint = hintsById[id] || {};
    blurbs[id] = buildBlurbForProduct(product, hint, {
      contextProduct,
      surface,
      useCase,
    });
  });

  return { blurbs, source: "fallback" };
}

export function stripMatchPrefix(label) {
  if (!label) return null;
  // Accept both the legacy "N% match ·" prefix (cached/stored copy that
  // hasn't rotated yet) and the current "N Fit score ·" prefix.
  return String(label)
    .replace(/^\d+%\s*match\s*·\s*/i, "")
    .replace(/^\d+\s*fit score\s*·\s*/i, "");
}

export { hasMatchOrFitScorePrefix };
